import React, { useState, useEffect, CSSProperties } from "react";
import axios from "axios";
import { Environment } from "@/services/environment";
import "./private-image.css";

interface PrivateImageProps {
  path: string;
  alt?: string;
  className?: string;
  style?: CSSProperties;
  width?: number | string;
  height?: number | string;
  onLoad?: () => void;
  onError?: (error: Error) => void;
  fallbackSrc?: string;
}

const PrivateImage: React.FC<PrivateImageProps> = ({
  path,
  alt = "Image",
  className,
  style,
  width,
  height,
  onLoad,
  onError,
  fallbackSrc,
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!path) {
      setIsLoading(false);
      setError(new Error("Path is required"));
      return;
    }

    const fetchImage = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await axios.get(
          `${Environment.hostApiWinPay}${path}`,
          {
            responseType: "blob",
          },
        );

        const blob = new Blob([response.data], {
          type: response.headers["content-type"] || "image/jpeg",
        });

        const url = URL.createObjectURL(blob);
        setImageUrl(url);

        onLoad?.();
      } catch (err) {
        const errorObj =
          err instanceof Error ? err : new Error("Failed to load image");
        setError(errorObj);
        onError?.(errorObj);
      } finally {
        setIsLoading(false);
      }
    };

    fetchImage();

    // Cleanup function to revoke object URL
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [path]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div
        className={`private-image-loading ${className || ""}`}
        style={{
          width: width || "100px",
          height: height || "100px",
          ...style,
        }}
      />
    );
  }

  if (error && !fallbackSrc) {
    return (
      <div
        className={`private-image-error ${className || ""}`}
        style={{
          width: width || "100px",
          height: height || "100px",
          ...style,
        }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
        <span>Không thể tải ảnh</span>
      </div>
    );
  }

  return (
    <img
      src={error && fallbackSrc ? fallbackSrc : imageUrl || ""}
      alt={alt}
      className={className}
      style={{
        width: width || "auto",
        height: height || "auto",
        ...style,
      }}
      onLoad={onLoad}
      onError={(e) => {
        const errorObj = new Error("Image failed to load");
        setError(errorObj);
        onError?.(errorObj);
      }}
    />
  );
};

export default PrivateImage;
